import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import ProfileFieldList from "./ProfileFieldList";
import { mediaUrl } from "../lib/api";
import { initialsFrom } from "../lib/utils";

/**
 * Read-only rendering of a player's cricket PROFILE (issue #508) — the
 * mirror of `CoachProfileView`, so a coach reads a player through a real
 * profile view instead of the ad-hoc badge cluster the CoachPortal header
 * carried before this slice.
 *
 * ============================================================
 * PROFILE, NOT IDENTITY — the line #508 draws and this file keeps
 * ============================================================
 * This is the PROFILE header: name, photo, playing role, country, batting
 * and bowling style, club, team, date of birth — the same fields the
 * player edits in `PlayerProfile`. It is deliberately NOT the shared
 * IDENTITY (Player Vision / Strengths / Game Identity), which #460 renders
 * on the coach portal's Identity tab through `SharedIdentityView`. The two
 * are distinct surfaces with distinct data: this file must never reach for
 * the identity sections and duplicate that tab into the header. That is why
 * it consumes `ProfileFieldList` (the shared row rhythm) and NOT
 * `SharedIdentityView` (the shared identity-section renderer).
 *
 * ============================================================
 * THE OPTIONAL-ROW PATTERN, borrowed from CoachProfileView
 * ============================================================
 * Every field but the name is optional, so an empty one is omitted rather
 * than rendered as an em dash — the old cluster showed `Batting: —` for a
 * player who never set one, which reads as an absence the platform is
 * pointing at. `ProfileFieldList` returns nothing when every row filtered
 * out, so a bare-name profile renders as a short header, not a wall of
 * blanks.
 *
 * `compact` is the inline variant (smaller avatar and name, a single-column
 * row list) for a future link-row caller, matching CoachProfileView's own
 * compact/full split; the CoachPortal header uses the full variant.
 *
 * THE PHOTO falls back to INITIALS from the player's name — never a stock
 * image (the responsive harness asserts no stock host anywhere). Unlike
 * CoachProfileView, the avatar renders even with no photo: this is a page
 * header that always shows a face, and the initials disc is that face.
 *
 * `nameTestId` keeps the CoachPortal header's `cp-selected-name` handle —
 * journeys 02/03/07/12/13/17/22 assert it — without this component knowing
 * the caller's testid scheme.
 *
 * `variant="coach-player"` (issue #973, D186) is the THIRD shape, next to
 * `compact`/full: the drawn `CoachPlayer.dc.html` card carries the same
 * seven fields this file already derives, but no avatar and no name — the
 * page's own header above it (`cp-selected-name`) already carries the
 * name, and duplicating a face here is not in the drawing. Reuses the
 * SAME `facts` derivation as every other variant (the one thing #508 asks
 * this file to own), inside the drawn card's own frame; every other
 * variant's output is unchanged by this branch's existence (the
 * `PlayerProfile` `kitType` precedent named in this header above).
 */
export default function PlayerProfileView({ player, compact = false, testId, nameTestId, variant = "default" }) {
  if (!player) return null;

  const facts = [
    player.playing_role ? { label: "Playing role", value: player.playing_role } : null,
    player.country ? { label: "Country", value: player.country } : null,
    player.batting_style ? { label: "Batting", value: player.batting_style } : null,
    player.bowling_style ? { label: "Bowling", value: player.bowling_style } : null,
    player.club ? { label: "Club", value: player.club } : null,
    /**
     * "Team", not "School / Academy" (#722). The client's words on the screen
     * board, 2026-09-17: "Need to replace School / Academy with Team as not
     * all users will be in a school or an academy." The COLUMN is still
     * `school` — renaming it would be a `player_profiles` migration for a
     * label change, and nothing the client asked for depends on the column's
     * name. `PlayerProfile.jsx` carries the same word on the form the player
     * types it into; a field renamed where it is entered and not where it is
     * read back would just move the confusion.
     */
    player.school ? { label: "Team", value: player.school } : null,
    player.date_of_birth ? { label: "Date of birth", value: player.date_of_birth } : null,
  ].filter(Boolean);

  if (variant === "coach-player") {
    return (
      <div
        className="min-w-0"
        style={{
          padding: 14,
          background: "#FFFFFF",
          border: "1px solid var(--nw-line)",
          borderRadius: 12,
        }}
        data-testid={testId}
      >
        <ProfileFieldList facts={facts} compact={false} />
      </div>
    );
  }

  return (
    <div
      className={`min-w-0 ${compact ? "space-y-2" : "flex items-center gap-5 flex-wrap"}`}
      data-testid={testId}
    >
      <Avatar
        className={`${compact ? "h-12 w-12" : "h-20 w-20"} shrink-0 border-4 border-[#D4A017]`}
        data-testid={testId ? `${testId}-photo` : undefined}
      >
        {player.profile_photo && <AvatarImage src={mediaUrl(player.profile_photo)} alt="" />}
        <AvatarFallback className={compact ? "text-sm font-semibold" : "text-xl font-semibold"}>
          {initialsFrom(player.full_name) || "P"}
        </AvatarFallback>
      </Avatar>

      <div className={compact ? "min-w-0 space-y-2" : "w-full sm:flex-1 sm:min-w-[220px] min-w-0 space-y-2"}>
        <h2
          className={`font-display font-black text-slate-900 break-words ${compact ? "text-base" : "text-2xl"}`}
          data-testid={nameTestId}
        >
          {player.full_name}
        </h2>
        <ProfileFieldList facts={facts} compact={compact} />
      </div>
    </div>
  );
}
